import { Button, Modal } from 'antd';
import { TBusinessType } from '../../entities/general.entities';
import { CloseIcon, Dropdown, Input, Subtitle } from '../../components/shared';
import { useState, useEffect } from 'react';
import { translate } from '../../lib/lang.helper';
import { useDispatch, useSelector } from 'react-redux';
import { selectLang } from '../../store/ui/ui.selector';
import { selectSpatStorages } from '../../store/farms/farms.selector';
import { defaultDateFormat, getUnitOfSpecie } from '../../lib/common.helpers';
import { getStageLabel, StageLabelSpan } from '../../components/CommonViews';
import { selectAccount } from '../../store/auth/auth.selector';
import { ISpatStorageResource } from '../../entities/farms.entities';
import ModalFeedbackView from '../../components/shared/feedback/ModalFeedbackView';
import { showFeedback } from '../../store/ui/ui.actions';
import { sendSingleRequest } from '../../apis';
import { loadSpatStorages } from '../../store/farms/farms.actions';
import SelectCropType from '../../components/farmform/SelectCropType';

interface IForm {
  condition?: string | null;
  weight: number;
  stage: number;
  source?: string;
}

const defaultForm: IForm = {
  weight: 0,
  stage: 1,
};

interface Props {
  visible: boolean;
  onClose: () => void;
  type?: TBusinessType;
}

const SpatsMergeModal = ({ visible, onClose, type: paramType }: Props) => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const spatStorages = useSelector(selectSpatStorages);
  const account = useSelector(selectAccount);
  const stageLabels = account?.stage_labels;
  const unitType = account?.display_unit;

  const [cropType, setCropType] = useState(paramType);
  const [disabled, setDisabled] = useState(false);
  const [spats, setSpats] = useState<ISpatStorageResource[]>([]);
  const [form, setForm] = useState<IForm>(defaultForm);
  const [weightInput, setWeightInput] = useState('0');

  useEffect(() => {
    setSpats([]);
    setForm(defaultForm);
    setWeightInput(defaultForm.weight.toString());
  }, [cropType]);

  const spatsOptions = spatStorages
    .filter(x => cropType && x.type === cropType)
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.id}, ${x.source}, ${defaultDateFormat(
        x.collection_date,
      )}, ${getStageLabel(stageLabels, x.stage)}`,
    }));

  const onSpatsChange = (selected: string[]) => {
    const selectedSpats = spatStorages.filter(x =>
      selected.includes(x.id.toString()),
    );
    setSpats(selectedSpats);
    const newWeight = selectedSpats.reduce((p, c) => p + c.weight, 0);
    setForm(prv => ({
      weight: newWeight,
      stage: selectedSpats.length > 0 ? selectedSpats[0].stage : prv.stage,
      source: selectedSpats.length > 0 ? selectedSpats[0].source : prv.source,
      condition:
        selectedSpats.length > 0 ? selectedSpats[0].condition : prv.condition,
    }));
    setWeightInput(newWeight.toString());
  };
  const showError = (message: string) =>
    dispatch(
      showFeedback({
        isMessageModal: true,
        message,
        type: 'error',
      }),
    );

  const confirmClick = async () => {
    if (!cropType) {
      showError(translate(lang, '_sel_crop_type'));
      return;
    }
    if (spats.length === 0) {
      showError(translate(lang, 'Please select spats'));
      return;
    }
    if (form.weight <= 0) {
      showError(translate(lang, 'Please enter weight'));
      return;
    }
    setDisabled(true);
    const batch_ids = spats.map(x => x.id);
    const response = await sendSingleRequest(
      { batch_ids, ...form },
      'POST',
      'api/farm/spats-merge',
      true,
    );
    if (response.status) {
      await dispatch(loadSpatStorages());
      dispatch(
        showFeedback({
          type: 'success',
          message: translate(lang, '_spats_merged_ok'),
          isMessage: true,
        }),
      );
      onClose();
    } else {
      showError(translate(lang, response.data?.message ?? 'Server error'));
      setDisabled(false);
    }
  };

  return (
    <Modal
      visible={visible}
      footer={null}
      onCancel={onClose}
      closable={true}
      closeIcon={<CloseIcon />}
      width={700}
    >
      <div className='wrap'>
        <div className='mb-28'>
          <Subtitle size={4} color='black-1' align='left' fontWeight={600}>
            {translate(lang, 'Merge Spat Storages')}
          </Subtitle>
        </div>
        <div className='mb-17'>
          <SelectCropType value={cropType} onChange={v => setCropType(v)} />
        </div>
        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Select spats')}
            mode='multiple'
            options={spatsOptions}
            showSearch={true}
            value={spats.map(x => x.id.toString()) as any}
            onChange={(v: any) => onSpatsChange(v)}
          />
        </div>
        <div className='mb-17'>
          <Input
            type='text'
            label={translate(lang, 'Source')}
            value={form.source ?? ''}
            onChange={e => setForm({ ...form, source: e.target.value })}
          />
        </div>
        <div className='mb-17'>
          <Input
            type='number'
            label={translate(lang, cropType === 'OYSTER' ? 'Pieces' : 'Weight')}
            value={weightInput}
            onChange={e => {
              const str = e.target.value;
              setWeightInput(str);
              const raw = Number(str);
              setForm(prv => ({
                ...prv,
                weight: isNaN(raw) ? 0 : raw,
              }));
            }}
            unit={
              cropType === 'OYSTER'
                ? 'pcs'
                : getUnitOfSpecie(unitType, 'weight_kg')
            }
          />
        </div>
        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Stage')}
            options={[1, 2, 3, 4].map(x => ({
              id: x.toString(),
              value: x.toString(),
              label: <StageLabelSpan stage={x} />,
            }))}
            value={form.stage.toString()}
            onChange={v => setForm({ ...form, stage: Number(v) })}
          />
        </div>
        <div className='mb-17'>
          <Dropdown
            label={translate(lang, 'Condition')}
            onChange={v => setForm({ ...form, condition: v as string })}
            options={[
              { id: 'Good', label: 'Good', value: 'Good' },
              { id: 'Average', label: 'Average', value: 'Average' },
              { id: 'Bad', label: 'Bad', value: 'Bad' },
            ]}
            value={form.condition ?? undefined}
          />
        </div>
        <div className='mt-32 d-flex justify-end'>
          <Button
            type='primary'
            disabled={disabled}
            onClick={confirmClick}
            size='large'
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default SpatsMergeModal;
